import React from "react";
import { AlertTriangle } from "lucide-react";
import { Card, EmptyState } from "../ui/Primitives";

const RELOAD_FLAG = "lrp.chunkReload";

function isChunkError(error) {
  const text = `${error && error.name} ${error && error.message}`;
  return /ChunkLoadError|Loading chunk|Loading CSS chunk|dynamically imported module/i.test(text);
}

/**
 * Catches render errors so the app never goes blank. A missing code chunk
 * (an old tab after a new deploy) triggers one automatic reload.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidMount() {
    // A successful load clears the guard so a later deploy can auto-reload again.
    this.clearTimer = setTimeout(() => {
      try {
        if (!this.state.error) sessionStorage.removeItem(RELOAD_FLAG);
      } catch (e) {
        // ignore
      }
    }, 10000);
  }

  componentWillUnmount() {
    clearTimeout(this.clearTimer);
  }

  componentDidCatch(error) {
    if (isChunkError(error)) {
      let reloaded = false;
      try {
        reloaded = sessionStorage.getItem(RELOAD_FLAG) === "1";
        sessionStorage.setItem(RELOAD_FLAG, "1");
      } catch (e) {
        // ignore
      }
      if (!reloaded) window.location.reload();
    }
  }

  componentDidUpdate(prevProps) {
    if (this.state.error && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <Card>
        <EmptyState
          icon={AlertTriangle}
          title="This page couldn't load"
          actions={
            <button
              type="button"
              className="btn btn--primary"
              onClick={() => {
                try {
                  sessionStorage.removeItem(RELOAD_FLAG);
                } catch (e) {
                  // ignore
                }
                window.location.reload();
              }}
            >
              Reload
            </button>
          }
        >
          {isChunkError(this.state.error)
            ? "A newer version of the app is available. Reload to continue."
            : "Something went wrong while showing this page. Your loans and settings are safe."}
        </EmptyState>
      </Card>
    );
  }
}
